const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getToken = () => localStorage.getItem('tennisbuzz_token');

const setToken = (token) => {
  if (token) {
    localStorage.setItem('tennisbuzz_token', token);
  } else {
    localStorage.removeItem('tennisbuzz_token');
  }
};

const headers = (json = true) => {
  const h = {};
  if (json) h['Content-Type'] = 'application/json';
  const token = getToken();
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
};

// Normalize _id to id recursively
const normalize = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(normalize);
  const out = { ...obj };
  if (out._id) { 
    out.id = String(out._id); // Convert ObjectId to string
  }
  Object.keys(out).forEach(k => {
    if (Array.isArray(out[k])) out[k] = out[k].map(normalize);
    else if (out[k] && typeof out[k] === 'object' && !Array.isArray(out[k])) out[k] = normalize(out[k]);
  });
  return out;
};

// Secure request interceptor
const request = async (url, options = {}) => {
  try {
    const res = await fetch(`${BASE}${url}`, { headers: headers(), ...options });
    const data = await res.json();
    
    // Handle 401 Unauthorized - token expired or invalid
    if (res.status === 401) {
      setToken(null);
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      }
      const error = new Error(data.message || 'Session expired. Please login again.');
      error.response = { data };
      throw error;
    }
    
    // Handle other error status codes
    if (!res.ok) {
      const error = new Error(data.message || `Request failed with status ${res.status}`);
      error.response = { data };
      throw error;
    }
    
    // Normalize all responses - handle nested data structures
    if (data.data) {
      if (typeof data.data === 'object' && !Array.isArray(data.data)) {
        Object.keys(data.data).forEach(key => {
          data.data[key] = normalize(data.data[key]);
        });
      } else {
        data.data = normalize(data.data);
      }
    }
    if (data.user) data.user = normalize(data.user);
    if (data.tournament) data.tournament = normalize(data.tournament);
    if (data.players) data.players = normalize(data.players);
    
    return data;
  } catch (error) {
    console.error('API Error:', error.message);
    throw error;
  }
};

// Export token management for auth operations
export { setToken };

const get = (url) => request(url);
const post = (url, body) => request(url, { method: 'POST', body: JSON.stringify(body) });
const put = (url, body) => request(url, { method: 'PUT', body: JSON.stringify(body) });
const del = (url) => request(url, { method: 'DELETE' });

// ── Auth ──
export const authAPI = {
  register: (data) => post('/auth/register', data),
  login: (data) => post('/auth/login', data),
  me: () => get('/auth/me'),
};

// ── Players ──
export const playerAPI = {
  getAll: (params = '') => get(`/players${params ? '?' + params : ''}`),
  getOne: (id) => get(`/players/${id}`),
  getDetails: (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return get(`/players/${id}/details${queryString ? '?' + queryString : ''}`);
  },
  create: (data) => post('/players', data),
  bulk: (players) => post('/players/bulk', { players }),
  update: (id, data) => put(`/players/${id}`, data),
  remove: (id) => del(`/players/${id}`),
};

// ── Tournaments ──
export const tournamentAPI = {
  getAll: (params = '') => get(`/tournaments${params ? '?' + params : ''}`),
  getStats: () => get('/tournaments/stats'),
  getOne: (id) => get(`/tournaments/${id}`),
  getDetails: (id) => get(`/tournaments/${id}/details`),
  getHomeMatches: () => get('/tournaments/home/matches'),
  getHomeSummary: () => get('/tournaments/home/summary'),
  getLiveSummary: () => get('/tournaments/live/summary'),
  getScheduleSummary: () => get('/tournaments/schedule/summary'),
  getOrganizerSummary: () => get('/tournaments/organizer/summary'),
  create: (data) => post('/tournaments', data),
  update: (id, data) => put(`/tournaments/${id}`, data),
  remove: (id) => del(`/tournaments/${id}`),
  join: (id, data) => post(`/tournaments/${id}/join`, data),
  leave: (id, data) => post(`/tournaments/${id}/leave`, data),
  setSeeds: (id, seeds) => put(`/tournaments/${id}/seeds`, { seeds }),
  generateBracket: (id) => post(`/tournaments/${id}/generate-bracket`, {}),
  getStandings: (id) => get(`/tournaments/${id}/standings`),
  checkIn: (id, data) => post(`/tournaments/${id}/checkin`, data),
  checkOut: (id, data) => post(`/tournaments/${id}/checkout`, data),
  addCoupon: (id, data) => post(`/tournaments/${id}/coupons`, data),
  deleteCoupon: (id, couponId) => del(`/tournaments/${id}/coupons/${couponId}`),
  applyCoupon: (id, data) => post(`/tournaments/${id}/coupons/apply`, data),
  useCoupon: (id, couponId) => post(`/tournaments/${id}/coupons/${couponId}/use`, {}),
  addAnnouncement: (id, data) => post(`/tournaments/${id}/announcements`, data),
  deleteAnnouncement: (id, annId) => del(`/tournaments/${id}/announcements/${annId}`),
  addDiscussion: (id, data) => post(`/tournaments/${id}/discussion`, data),
  reset: (id, flags) => post(`/tournaments/${id}/reset`, flags),
  completeTournament: (id) => post(`/tournaments/${id}/complete`, {}),
  regenerateTournamentSummary: (id) => post(`/tournaments/${id}/regenerate-summary`, {}),
  bulkAddPlayers: (id, players) => post(`/tournaments/${id}/bulk-add-players`, { players }),
  generateAIDraw: (id) => post(`/tournaments/${id}/generate-ai-draw`, {}),
};

// ── Matches ──
export const matchAPI = {
  getAll: (params = '') => get(`/matches${params ? '?' + params : ''}`),
  getOne: (id) => get(`/matches/${id}`),
  getDetails: (id) => get(`/matches/${id}/details`),
  getByTournament: (tid) => get(`/matches/tournament/${tid}`),
  getByPlayer: (pid) => get(`/matches/player/${pid}`),
  getHeadToHead: (p1, p2) => get(`/matches/head-to-head/${p1}/${p2}`),
  create: (data) => post('/matches', data),
  updateScore: (id, data) => put(`/matches/${id}/score`, data),
  resolveSpecial: (id, data) => put(`/matches/${id}/resolve`, data),
  swapPlayers: (data) => post('/matches/swap-players', data),
  swapMatches: (data) => post('/matches/swap-matches', data),
  regenerateCommentary: (id) => post(`/matches/${id}/regenerate-commentary`, {}),
  predict: (id, pick) => post(`/matches/${id}/predict`, { pick }),
  remove: (id) => del(`/matches/${id}`),
};

// ── Comments ──
export const commentAPI = {
  getByMatch: (matchId, params = '') => get(`/comments/match/${matchId}${params ? '?' + params : ''}`),
  create: (matchId, data) => post(`/comments/match/${matchId}`, data),
  remove: (id) => del(`/comments/${id}`),
};

// ── Notifications ──
export const notificationAPI = {
  create: (tournamentId, data) => post(`/notifications/tournament/${tournamentId}`, data),
  getForPlayer: (playerId) => get(`/notifications/player/${playerId}`),
  markRead: (id, data) => put(`/notifications/${id}/read`, data),
  getVapidKey: () => get('/notifications/vapid-key'),
  subscribe: (subscription) => post('/notifications/subscribe', { subscription }),
  unsubscribe: (endpoint) => post('/notifications/unsubscribe', { endpoint }),
  follow: (tournamentId) => post(`/notifications/follow/${tournamentId}`, {}),
  unfollow: (tournamentId) => del(`/notifications/follow/${tournamentId}`),
  followPlayer: (playerId) => post(`/notifications/follow-player/${playerId}`, {}),
  unfollowPlayer: (playerId) => del(`/notifications/follow-player/${playerId}`),
  getFollowed: () => get('/notifications/followed'),
};

// ── Admin ──
export const adminAPI = {
  switchDb: (env) => post('/admin/switch-db', { env }),
  health: () => get('/health'),
};

// ── Search ──
export const searchAPI = {
  search: (q) => get(`/search?q=${encodeURIComponent(q)}`),
};

// ── SSE helper ──
export const createMatchSSE = (onUpdate) => {
  const url = `${BASE}/matches/live-stream`;
  const es = new EventSource(url);
  es.addEventListener('match-update', (e) => {
    try { onUpdate(JSON.parse(e.data)); } catch {}
  });
  return es;
};
